import Button from "@/components/Button"
import Navbar from "@/components/Navbar"
import Booking from "@/components/Booking"
import { ServiceProps } from "@/screens/services/PropsServices"
import { useState, useRef, useEffect } from "react"
import { useLocation } from 'react-router-dom'
import { getServicePricing } from './NewPrices'
import { getServiceOptions } from './NewServices'
import PriceCard from '@/components/PriceCard'
import ServiceFAQ from "@/components/ServiceFAQ"
import BeforeAfterGallery from "@/components/BeforeAfterGallery"
import Footer, { FooterProps } from '@/components/Footer'
import FabricServiceCard from "@/components/FabricServiceCard"
import ProjectGalleryCard from '@/components/ProjectGalleryCard'
import ScrollableContainer from '@/components/ScrollableContainer'
import SEO from "@/components/SEO"
import LoadingScreen from "@/components/LoadingScreen"
import { useTranslation } from 'react-i18next'

// Import car images
import compactCarImg from '@/assets/images/compact-car.png';
import sedanCarImg from '@/assets/images/sedan-car.png';
import suvCarImg from '@/assets/images/suv-car.png';
import mpvCarImg from '@/assets/images/mpv-car.png';
import vanCarImg from '@/assets/images/van.png';

// Import fabric images for the sub-service pages
import sofaImg from '@/assets/images/sofa-bg-1.jpeg';
import tilamImg from '@/assets/images/tilam-bg-1.jpeg';
import keretaImg from '@/assets/images/kereta-kerusi-bg-1.jpeg';
import lainLainImg from '@/assets/images/carpet-bg-1.jpeg';

// Add sanitise images (you'll need to add these)
import hicomyamahaImg from '@/assets/images/hicom-yamaha-1.jpeg';
import furuturepreludeImg from '@/assets/images/future-prelude-sb-1.jpeg';
import agriasiaImg from '@/assets/images/agri-asia-1.jpeg';
import petronasImg from '@/assets/images/petronas-1.jpeg';
import protonImg from '@/assets/images/proton-1.jpeg';
import carlsjrImg from '@/assets/images/carlsjr-1.jpeg';

// Add pest control images
import ratsImg from '@/assets/images/rats-1.jpg';
import cockroachesImg from '@/assets/images/cockroaches-1.jpg';
import bed_bugImg from '@/assets/images/bed_bug-1.jpg';
import termitesImg from '@/assets/images/termites.jpg';
import antsImg from '@/assets/images/ants-1.jpg';
import cicakImg from '@/assets/images/cicak-1.jpg';

// Add pest control gallery images (at the top with other image imports)
import pestControl1 from '@/assets/images/pest.jpeg';

// Map of image paths to actual imports
const imageMap: Record<string, string> = {
  'compact-car.png': compactCarImg,
  'sedan-car.png': sedanCarImg,
  'suv-car.png': suvCarImg,
  'mpv-car.png': mpvCarImg,
  'van.png': vanCarImg,
};

// Image mapping object for pest control services
const pestControlImages: Record<string, string> = {
  'Kawalan Tikus': ratsImg,
  'Rat Control': ratsImg,
  'Kawalan Lipas': cockroachesImg,
  'Cockroach Control': cockroachesImg,
  'Kawalan Anai-Anai': termitesImg,
  'Termite Control': termitesImg,
  'Kawalan Pepijat': bed_bugImg,
  'Bed Bug Control': bed_bugImg,
  'Kawalan Semut': antsImg,
  'Ant Control': antsImg,
  'Kawalan Cicak': cicakImg,
  'Lizard Control': cicakImg
};

// Image mapping objects for gallery data (using slugs instead of titles)
const sanitiseGalleryImages: Record<string, string> = {
  'hicom-yamaha': hicomyamahaImg,
  'future-prelude-sb': furuturepreludeImg,
  'agri-asia': agriasiaImg,
  'petronas': petronasImg,
  'proton': protonImg,
  'carls-jr': carlsjrImg
};

const pestControlGalleryImages: Record<string, string> = {
  'residential-pest-control': pestControl1,
  'commercial-pest-control': pestControl1
};

const fabricSubServiceImages: Record<string, string> = {
  'sofa': sofaImg,
  'tilam': tilamImg,
  'kereta': keretaImg,
  'lain-lain': lainLainImg
};

// At the top of the file, add this type that extends ServiceProps
type ServicePageProps = ServiceProps & {
  footer_bg: string;
  footer_text_color?: string; // Add optional text color prop
};

// First, create a component for the pest control service cards
const PestControlServiceCard = ({ title, imageSrc, description }: { 
  title: string; 
  imageSrc: string; 
  description: string; 
}) => (
  <div className="bg-white rounded-lg shadow-lg hover:shadow-xl overflow-hidden transition-all duration-300 group">
    <div className="h-48 w-full relative">
      <img 
        src={imageSrc}
        alt={title} 
        className="w-full h-full object-cover"
      />
      {/* Add overlay with description on hover */}
      <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center p-4">
        <p className="text-white text-center text-sm">{description}</p>
      </div>
    </div>
    <div className="p-6 group-hover:bg-gray-50 transition-colors duration-300">
      <h3 className="text-xl font-semibold text-gray-800 group-hover:text-trhine-brown-100 transition-colors duration-300">{formatTextWithItalics(title)}</h3>
    </div>
  </div>
);

interface ServiceWithPrice {
  id: string;
  title: string;
  price: string;
}

// Add this mapping object at the top of the file
const serviceToFooterMapping: Record<string, FooterProps['currentService']> = {
  'fabric': 'fabric',
  'pest-control': 'pestcontrol',
  'car-polish': 'cardetailing',
  'sanitise': 'sanitise',
  'aircond': 'aircond'
};

// SEO key mapping for translation keys
const seoKeyMap: Record<string, string> = {
  'car-polish': 'carPolish',
  'aircond': 'aircond',
  'fabric': 'fabric',
  'sanitise': 'sanitise',
  'pest-control': 'pestControl'
};

// Add this function near the top of the file with other utility functions
const formatTextWithItalics = (text: string): React.ReactNode => {
  // Check if text contains backticks
  if (!text.includes('`')) return text;
  
  // Split the text by backticks
  const parts = text.split('`');
  const result: React.ReactNode[] = [];
  
  // Alternate between normal and italic text
  parts.forEach((part, index) => {
    if (index % 2 === 0) {
      // Even indices are regular text
      result.push(part);
    } else {
      // Odd indices should be italic
      result.push(<i key={index}>{part}</i>);
    }
  });
  
  return result;
};

const ServicePage = ({
  title,
  description,
  backgroundImage,
  primaryButtonActive,
  secondaryButtonActive,
  buttonColor,
  id,
  footer_bg,
  footer_text_color = 'text-white' // Default to white text
}: ServicePageProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<ServiceWithPrice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const pricingRef = useRef<HTMLDivElement>(null);

  // Check if we should skip loading (coming from back navigation)
  const shouldSkipLoading = location.state?.skipLoading;

  // Loading effect
  useEffect(() => {
    if (shouldSkipLoading) {
      setIsLoading(false);
      // Clear the state after using it
      window.history.replaceState({}, document.title);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800); // Show loading screen for 800ms

    return () => clearTimeout(timer);
  }, [id, shouldSkipLoading]); // Re-trigger when service ID changes

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Get translated pricing data
  const servicePricing = getServicePricing(t);
  const pricing = servicePricing[id];

  // Get translated service options
  const serviceOptions = getServiceOptions(t);

  // Get translated data arrays
  const pestControlServices = (t('pestControlServices', { returnObjects: true }) as Array<{ title: string; description: string }>).map(service => ({
    ...service,
    imageSrc: pestControlImages[service.title]
  }));

  const sanitiseGallery = (t('sanitiseGallery', { returnObjects: true }) as Array<{ title: string; slug: string; description: string; location: string }>).map(project => ({
    ...project,
    imageSrc: sanitiseGalleryImages[project.slug]
  }));

  const pestControlGallery = (t('pestControlGallery', { returnObjects: true }) as Array<{ title: string; slug: string; description: string; location: string }>).map(project => ({
    ...project,
    imageSrc: pestControlGalleryImages[project.slug]
  }));

  const fabricSubServices = (t('fabricSubServices', { returnObjects: true }) as Array<{ title: string; slug: string; description: string }>).map(service => ({
    ...service,
    imageSrc: fabricSubServiceImages[service.slug]
  }));

  // Separate main cards from add-on cards (using ID prefix)
  const mainCards = pricing?.cards.filter(card => !card.id.startsWith('addon-')) || [];
  const addOnCards = pricing?.cards.filter(card => card.id.startsWith('addon-')) || [];

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookingClick = (serviceIds: string[] = []) => {

    if (serviceIds.length === 0) {
        setIsBookingOpen(true);
        return;
    }

    const clickedServiceId = serviceIds[0];

    // Find service by ID in all cards
    const serviceDetails = pricing?.cards.find(card => card.id === clickedServiceId);

    // For sanitise service, don't pre-select pricing cards - users must use the calculator
    if (serviceDetails && id !== 'sanitise') {
        setSelectedServices(prev => {
            const exists = prev.some(s => s.id === serviceDetails.id);

            const newSelections = exists
                ? prev.filter(s => s.id !== serviceDetails.id)
                : [...prev, { id: serviceDetails.id, title: serviceDetails.title, price: serviceDetails.price }];

            return newSelections;
        });
    }

    setIsBookingOpen(true);
  };

  // Determine if we should show car images (only for Polish Kereta service)
  const shouldShowCarImages = id === 'car-polish';
  
  // Special case for fabric service
  if (id === 'fabric') {
    return (
      <div className="min-h-screen">
        <SEO
          title={t(`seo.${seoKeyMap[id]}.title`)}
          description={t(`seo.${seoKeyMap[id]}.description`)}
          keywords={t(`seo.${seoKeyMap[id]}.keywords`)}
          type="service"
          lang={i18n.language as 'ms' | 'en'}
          serviceName={title}
          serviceType={id}
        />
        <Navbar />
        {/* Hero Section - This will be shown first */}
        <section
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="min-h-screen flex flex-col justify-center relative"
        >
          <div className="container mx-auto px-4 py-24 md:py-16 ">
            <div className="pt-16 md:pt-0">
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-6 mt-20 break-words hyphens-auto">
                {formatTextWithItalics(title)}
              </h1>
              <p className="text-white text-lg md:text-xl mb-12 w-full text-left md:text-justify whitespace-pre-line leading-relaxed tracking-wide">
                {formatTextWithItalics(description)}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button
                  text={t('servicePage.bookNow')}
                  active={primaryButtonActive}
                  className={`${primaryButtonActive ? buttonColor : ''} ${id === 'fabric' ? 'text-black' : ''}`}
                  onClick={() => handleBookingClick()}
                  serviceId={id}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Fabric Sub-Services Section */}
        <section className="py-16 px-8 bg-gray-100">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">{t('servicePage.chooseServiceType')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {fabricSubServices.map((subService, index) => (
                <FabricServiceCard
                  key={index}
                  title={subService.title}
                  imageSrc={subService.imageSrc}
                  slug={subService.slug}
                  description={subService.description}
                />
              ))}
            </div>
          </div>
        </section>

        <Booking
          isOpen={isBookingOpen}
          onClose={() => {
            setIsBookingOpen(false);
            setSelectedServices([]);
          }}
          serviceId={id}
          selectedServices={selectedServices}
        />

        {/* FAQ Section */}
        {serviceOptions[id]?.faqs && serviceOptions[id].faqs.length > 0 && (
          <ServiceFAQ
            faqs={serviceOptions[id].faqs}
            dropdownStyles={serviceOptions[id].dropdownStyles}
          />
        )}

        <Footer
          footer_bg={footer_bg}
          text_color={footer_text_color}
          currentService={serviceToFooterMapping[id]}
        />
      </div>
    );
  }

  // Regular service page rendering for non-fabric services
  return (
    <div className="min-h-screen">
      <SEO
        title={t(`seo.${seoKeyMap[id]}.title`)}
        description={t(`seo.${seoKeyMap[id]}.description`)}
        keywords={t(`seo.${seoKeyMap[id]}.keywords`)}
        type="service"
        lang={i18n.language as 'ms' | 'en'}
        serviceName={title}
        serviceType={id}
      />
      <Navbar />
      {/* Hero Section - This will be shown first */}
      <section
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.73)), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="min-h-screen flex flex-col justify-center relative"
      >
        <div className="container mx-auto px-4 py-24 md:py-16 ">
          <div className="pt-16 md:pt-0">
            <h1 className="text-white text-4xl md:text-5xl font-bold mb-6 mt-20 break-words hyphens-auto">
              {formatTextWithItalics(title)}
            </h1>
            <p className="text-white text-lg md:text-xl mb-12 w-full text-left md:text-justify whitespace-pre-line leading-relaxed tracking-wide">
              {formatTextWithItalics(description)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                text={t('servicePage.bookNow')}
                active={primaryButtonActive}
                className={`${primaryButtonActive ? buttonColor : ''} ${id === 'fabric' ? 'text-black font-bold' : ''}`}
                onClick={() => handleBookingClick()}
                serviceId={id}
              />
              <Button
                text={t('servicePage.viewPrice')}
                active={secondaryButtonActive}
                className={secondaryButtonActive ? buttonColor : ''}
                onClick={scrollToPricing}
                serviceId={id}
              />
            </div>
          </div>
        </div>
      </section>

      <Booking 
        isOpen={isBookingOpen}
        onClose={() => {
          setIsBookingOpen(false);
          setSelectedServices([]);
        }}
        serviceId={id}
        selectedServices={selectedServices}
      />

      {/* Sanitise Service Cards Section */}
      {id === 'sanitise' && (
        <section id="sanitise-gallery" className="py-16 px-8 bg-gray-100 scroll-mt-16">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">{t('servicePage.sanitiseGalleryTitle')}</h2>
            <p className="text-center text-gray-500 text-xs md:text-sm mb-4 mt-2 px-4">{t('servicePage.clickForDetails')}</p>
            {sanitiseGallery.length === 1 ? (
              <div className="flex justify-center">
                <div className="w-full max-w-md">
                  <ProjectGalleryCard
                    {...sanitiseGallery[0]}
                    serviceType="sanitise"
                  />
                </div>
              </div>
            ) : (
              <ScrollableContainer>
                <div className="flex gap-6 flex-nowrap">
                  {sanitiseGallery.map((project, index) => (
                    <div key={index} className="min-w-[320px] md:min-w-[350px]">
                      <ProjectGalleryCard
                        {...project}
                        serviceType="sanitise"
                      />
                    </div>
                  ))}
                </div>
              </ScrollableContainer>
            )}
          </div>
        </section>
      )}

      {/* Pest Control Cards Section */}
      {id === 'pest-control' && (
        <>
          {/* Pest Control Services Section - Shows all types of pest control */}
          <section className="py-16 px-8 bg-white">
            <div className="max-w-[1200px] mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">{t('servicePage.pestControlTypesTitle')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {pestControlServices.map((service, index) => (
                  <div key={index}>
                    <PestControlServiceCard
                      title={service.title}
                      imageSrc={service.imageSrc}
                      description={service.description}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Pest Control Gallery Section - Shows completed projects */}
          <section id="pest-control-gallery" className="py-16 px-8 bg-gray-100 scroll-mt-16">
            <div className="max-w-[1200px] mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">{t('servicePage.pestControlGalleryTitle')}</h2>
              <p className="text-center text-gray-500 text-xs md:text-sm mb-4 mt-2 px-4">{t('servicePage.clickForDetails')}</p>
              {pestControlGallery.length === 1 ? (
                <div className="flex justify-center">
                  <div className="w-full max-w-md">
                    <ProjectGalleryCard
                      {...pestControlGallery[0]}
                      serviceType="pest-control"
                    />
                  </div>
                </div>
              ) : (
                <ScrollableContainer>
                  <div className="flex gap-6 flex-nowrap">
                    {pestControlGallery.map((project, index) => (
                      <div key={index} className="min-w-[320px] md:min-w-[350px]">
                        <ProjectGalleryCard
                          {...project}
                          serviceType="pest-control"
                        />
                      </div>
                    ))}
                  </div>
                </ScrollableContainer>
              )}
            </div>
          </section>
        </>
      )}

      {/* Before After Gallery Section - For all services except sanitise */}
      {id !== 'sanitise' && serviceOptions[id]?.beforeAfterImages && serviceOptions[id].beforeAfterImages.length > 0 && (
        <BeforeAfterGallery 
          images={serviceOptions[id].beforeAfterImages} 
          activeButtonColor={serviceOptions[id].beforeAfterButtonColor}
        />
      )}

      {/* Pricing Section - Modified to include special case for pest control */}
      {pricing && (
        <div 
          ref={pricingRef} 
          className={`pt-8 py-32 px-8 ${pricing.backgroundColor}`}
        >
          <div className="max-w-[1200px] mx-auto">
            <h1 className="text-[40px] font-bold text-center text-white mb-8">{t('servicePage.servicePriceTitle')}</h1>

            {/* Services Section */}
            <div className="mb-12 space-y-6">
              {/* Special pricing display for pest control */}
              {id === 'pest-control' ? (
                <div className="bg-gray-800 rounded-lg p-8 max-w-2xl mx-auto">
                  {/* Included Services */}
                  {pricing.includedServices && pricing.includedServices.length > 0 && (
                    <>
                      <h2 className="text-white text-xl font-semibold mb-4">{t('servicePage.includedServices')}</h2>
                      <ul className="list-disc pl-5 space-y-2 mb-8">
                        {pricing.includedServices.map((service, idx) => (
                          <li key={idx} className="text-white text-[16px]">{formatTextWithItalics(service)}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* Centralized Price and Booking Button */}
                  <div className="text-center">
                    <p className="text-white text-lg font-bold mb-4">
                      {t('servicePage.startingFrom')}
                      <span className="text-red-500 text-2xl font-bold">
                      {t('servicePage.startingFromValue')}!
                      </span>
                    </p>
                    <Button
                      text={t('servicePage.bookNow')}
                      active={true}
                      className={`${pricing.buttonClassName} font-bold`}
                      onClick={() => handleBookingClick()}
                      serviceId={id}
                    />
                  </div>
                </div>
              ) : (
                // Original pricing display for other services
                <>
                  {/* Included Services */}
                  {pricing.includedServices && pricing.includedServices.length > 0 && (
                    <div className="bg-gray-800 rounded-lg p-6 max-w-2xl mx-auto">
                      <h2 className="text-white text-xl font-semibold mb-4">{t('servicePage.includedServices')}</h2>
                      <ul className="list-disc pl-5 space-y-2">
                        {pricing.includedServices.map((service, idx) => (
                          <li key={idx} className="text-white text-[16px]">{formatTextWithItalics(service)}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Excluded Services */}
                  {pricing.excludedServices && pricing.excludedServices.length > 0 && (
                    <div className="bg-gray-800 rounded-lg p-6 max-w-2xl mx-auto">
                      <h2 className="text-white text-xl font-semibold mb-4">{t('servicePage.excludedServices')}</h2>
                      <ul className="list-disc pl-5 space-y-2">
                        {pricing.excludedServices.map((service, idx) => (
                          <li key={idx} className="text-white text-[16px]">{formatTextWithItalics(service)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Main pricing cards - Only show for non-pest-control services */}
            {id !== 'pest-control' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {mainCards.map((card) => {
                  return (
                    <PriceCard
                      key={card.id}
                      {...card}
                      buttonClassName={pricing.buttonClassName}
                      onBookingClick={() => handleBookingClick([card.id])}
                      showCarImage={shouldShowCarImages && !!card.imagePath}
                      carImageSrc={card.imagePath ? imageMap[card.imagePath] : undefined}
                    />
                  );
                })}
              </div>
            )}

            {/* Add-Ons section - Only show for non-pest-control services */}
            {id !== 'pest-control' && addOnCards.length > 0 && (
              <div className="mt-16">
                <h2 className="text-[32px]   font-bold text-center text-white mb-8">{t('servicePage.addOns')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {addOnCards.map((card) => (
                    <div key={card.id} className="bg-gray-800 rounded-tr-[24px] rounded-bl-[24px] p-6 w-full">
                      <h3 className="text-white text-[20px] font-bold mb-4">
                        {formatTextWithItalics(card.title.replace('Add On : ', ''))}
                      </h3>
                      <div className="flex justify-between items-center">
                        <span className="text-white text-[20px] font-bold">{card.price}</span>
                        <button
                          className={`${pricing.buttonClassName} text-[12px] px-4 py-1.5`}
                          onClick={() => handleBookingClick([card.id])}
                        >
                          {t('servicePage.book')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQ Section */}
      {serviceOptions[id]?.faqs && serviceOptions[id].faqs.length > 0 && (
        <ServiceFAQ 
          faqs={serviceOptions[id].faqs} 
          dropdownStyles={serviceOptions[id].dropdownStyles}
        />
      )}

      <Footer 
        footer_bg={footer_bg} 
        text_color={footer_text_color}
        currentService={serviceToFooterMapping[id]}
      />
    </div>
  )
}

export default ServicePage 